import React, { useCallback, useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isCodeNode } from '@lexical/code';
import { $getRoot } from 'lexical';
import { $createDiffHunkNode } from '../customNodes/DiffHunkNode/DiffHunkNode';

/**
 * Converts root-level ```diff CodeNodes into interactive DiffHunkNodes.
 *
 * Important: do not latch a "converted" flag when nothing is found. When a
 * `reviewDiffDocument` arrives on an already-mounted editor, EditingPlugin
 * reloads markdown in a later update. A one-shot "nothing found → stop
 * listening" gate races that reload and leaves the editor locked in review
 * mode with no Accept/Reject controls.
 *
 * Conversion is idempotent: once a CodeNode is replaced, later passes are
 * no-ops. We only call `editor.update` when a read pass finds work, so the
 * update listener cannot loop.
 */
export const DiffHunkPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();

  const convertDiffCodeBlocks = useCallback(() => {
    let needsConversion = false;
    editor.getEditorState().read(() => {
      needsConversion = $getRoot()
        .getChildren()
        .some(node => $isCodeNode(node) && node.getLanguage() === 'diff');
    });
    if (!needsConversion) {
      return;
    }

    editor.update(() => {
      const root = $getRoot();
      for (const node of root.getChildren()) {
        if (!$isCodeNode(node) || node.getLanguage() !== 'diff') {
          continue;
        }
        const diffHunkNode = $createDiffHunkNode(node.getTextContent());
        node.insertBefore(diffHunkNode);
        node.remove();
      }
    });
  }, [editor]);

  useEffect(() => {
    convertDiffCodeBlocks();
    return editor.registerUpdateListener(({ tags }) => {
      if (tags.has('historic') || tags.has('collaboration')) {
        return;
      }
      convertDiffCodeBlocks();
    });
  }, [editor, convertDiffCodeBlocks]);

  return null;
};
